import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Loader2, Users, Info, Search, X } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import api from '../../services/api';
import { grupoService, Grupo, META_LIMITE_PARTICIPANTES } from '../../services/grupoService';

interface InstanciaOption {
  id: string;
  nome: string;
  tipoApi: 'UAZAPI' | 'META';
  isOnBizApp?: boolean;
}

interface CriarGrupoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  instancias: InstanciaOption[];
  instanciaPadraoId?: string;
  onCriado: (grupo: Grupo) => void;
}

interface ContatoBusca {
  id: string;
  nome?: string | null;
  telefone: string;
  telefoneValido: boolean;
}

// Número internacional sem '+': 8 a 15 dígitos (E.164)
const numeroValido = (numero: string) => numero.length >= 8 && numero.length <= 15;

const extrairNumeros = (texto: string): string[] => {
  return texto
    .split(/[\n,;]/)
    .map((linha) => linha.replace(/\D/g, ''))
    .filter((numero) => numero.length > 0);
};

export const CriarGrupoDialog: React.FC<CriarGrupoDialogProps> = ({
  open,
  onOpenChange,
  instancias,
  instanciaPadraoId,
  onCriado,
}) => {
  const { toast } = useToast();
  const [instanciaId, setInstanciaId] = useState(instanciaPadraoId || '');
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [aprovacaoEntrada, setAprovacaoEntrada] = useState(false);
  const [participantesTexto, setParticipantesTexto] = useState('');
  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState<ContatoBusca[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [selecionados, setSelecionados] = useState<ContatoBusca[]>([]);
  const [salvando, setSalvando] = useState(false);

  React.useEffect(() => {
    if (open) {
      setInstanciaId(instanciaPadraoId || instancias[0]?.id || '');
      setNome('');
      setDescricao('');
      setAprovacaoEntrada(false);
      setParticipantesTexto('');
      setBusca('');
      setResultados([]);
      setSelecionados([]);
    }
  }, [open, instanciaPadraoId, instancias]);

  const instanciaSelecionada = instancias.find((i) => i.id === instanciaId);
  const ehMeta = instanciaSelecionada?.tipoApi === 'META';
  const semSuporteGrupos = ehMeta && !!instanciaSelecionada?.isOnBizApp;
  const numerosDigitados = extrairNumeros(participantesTexto);
  const numerosInvalidos = numerosDigitados.filter((n) => !numeroValido(n));
  // Contatos escolhidos na busca + números digitados, sem duplicatas
  const participantes = Array.from(new Set([
    ...selecionados.map((c) => c.telefone),
    ...numerosDigitados.filter(numeroValido),
  ]));

  // Busca de contatos existentes (com debounce)
  React.useEffect(() => {
    const termo = busca.trim();
    if (termo.length < 2) {
      setResultados([]);
      return;
    }
    let cancelado = false;
    setBuscando(true);
    const timer = setTimeout(async () => {
      try {
        const response = await api.get('/contatos/busca-leve', { params: { search: termo, limit: 20 } });
        if (!cancelado) setResultados(response.data?.contatos || []);
      } catch {
        if (!cancelado) setResultados([]);
      } finally {
        if (!cancelado) setBuscando(false);
      }
    }, 300);
    return () => {
      cancelado = true;
      clearTimeout(timer);
    };
  }, [busca]);

  const selecionarContato = (contato: ContatoBusca) => {
    if (!contato.telefoneValido) {
      toast({
        title: 'Contato sem telefone',
        description: 'Este contato não tem um número de telefone resolvido e não pode ser adicionado ao grupo.',
        variant: 'destructive',
      });
      return;
    }
    setSelecionados((prev) => (prev.some((c) => c.telefone === contato.telefone) ? prev : [...prev, contato]));
    setBusca('');
    setResultados([]);
  };

  const handleCriar = async () => {
    if (!instanciaId) {
      toast({ title: 'Aviso', description: 'Selecione uma instância', variant: 'destructive' });
      return;
    }
    if (semSuporteGrupos) {
      toast({
        title: 'Instância sem suporte a grupos',
        description: 'Números em coexistência com o app WhatsApp Business não podem criar grupos pela API oficial.',
        variant: 'destructive',
      });
      return;
    }
    if (!nome.trim()) {
      toast({ title: 'Aviso', description: 'Informe o nome do grupo', variant: 'destructive' });
      return;
    }
    if (!ehMeta && participantes.length === 0) {
      toast({ title: 'Aviso', description: 'Adicione ao menos um participante (busque um contato ou digite um número)', variant: 'destructive' });
      return;
    }
    if (!ehMeta && numerosInvalidos.length > 0) {
      toast({
        title: 'Número inválido',
        description: `Use o formato internacional com 8 a 15 dígitos (ex.: 5521987654321). Revise: ${numerosInvalidos.join(', ')}`,
        variant: 'destructive',
      });
      return;
    }

    setSalvando(true);
    try {
      const response = ehMeta
        ? await grupoService.criar({
            instanciaMetaId: instanciaId,
            nome: nome.trim(),
            descricao: descricao.trim() || undefined,
            aprovacaoEntrada,
          })
        : await grupoService.criar({ instanciaUazapiId: instanciaId, nome: nome.trim(), participantes });
      toast({
        title: response.status === 202 ? 'Criação solicitada' : 'Grupo criado',
        description: response.status === 202
          ? `"${nome.trim()}" está sendo criado pela Meta. O link de convite aparecerá nos detalhes do grupo.`
          : `"${nome.trim()}" foi criado com sucesso.`,
      });
      onCriado(response.data.grupo);
      onOpenChange(false);
    } catch (error: any) {
      const dados = error?.response?.data;
      const mensagem = dados?.message || 'Não foi possível criar o grupo.';
      // Número sem Conta Comercial Oficial: o ajuste é no painel da Meta, então
      // oferecemos o passo a passo numa nova aba.
      const naoElegivel = dados?.codigo === 'META_GROUPS_NOT_ELIGIBLE';
      const linkAjuda = `/ajuda/meta-grupos${dados?.obaStatus ? `?status=${encodeURIComponent(dados.obaStatus)}` : ''}`;
      toast({
        title: naoElegivel ? 'Número não liberado para grupos' : 'Erro ao criar grupo',
        description: naoElegivel ? (
          <div className="space-y-2">
            <p>{mensagem}</p>
            <a
              href={linkAjuda}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block font-semibold underline underline-offset-2"
            >
              Clique aqui para passo a passo
            </a>
          </div>
        ) : mensagem,
        variant: 'destructive',
        // Tempo para ler e clicar no link
        ...(naoElegivel ? { duration: 15000 } : {}),
      });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] bg-white dark:bg-slate-800 dark:border-slate-700">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 dark:text-white">
            <Users className="h-5 w-5" />
            Novo Grupo
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label className="dark:text-gray-200" htmlFor="grupo-instancia">Instância</Label>
            <Select value={instanciaId} onValueChange={setInstanciaId}>
              <SelectTrigger id="grupo-instancia" className="dark:bg-slate-900 dark:border-slate-700 dark:text-white">
                <SelectValue placeholder="Selecione a instância" />
              </SelectTrigger>
              <SelectContent className="dark:bg-slate-800 dark:border-slate-700 dark:text-gray-100">
                {instancias.map((inst) => (
                  <SelectItem key={inst.id} value={inst.id} disabled={inst.tipoApi === 'META' && !!inst.isOnBizApp}>
                    {inst.nome}{inst.tipoApi === 'META' ? (inst.isOnBizApp ? ' (API oficial · coexistência: sem grupos)' : ' (API oficial)') : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="dark:text-gray-200" htmlFor="grupo-nome">Nome do grupo</Label>
            <Input className="dark:bg-slate-700 dark:border-slate-600 dark:text-white dark:placeholder:text-gray-400"
              id="grupo-nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Ex: Suporte VIP"
              maxLength={ehMeta ? 128 : 25}
            />
          </div>

          {ehMeta ? (
            <>
              <div className="space-y-2">
                <Label className="dark:text-gray-200" htmlFor="grupo-descricao">Descrição (opcional)</Label>
                <Textarea className="dark:bg-slate-700 dark:border-slate-600 dark:text-white dark:placeholder:text-gray-400"
                  id="grupo-descricao"
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  rows={3}
                  maxLength={2048}
                />
              </div>

              <div className="flex items-center justify-between rounded-lg border p-3 dark:border-slate-700">
                <div>
                  <Label className="dark:text-gray-200">Exigir aprovação para entrar</Label>
                  <p className="text-xs text-muted-foreground dark:text-gray-400">
                    Quem abrir o link de convite fica pendente até você aprovar. Só pode ser definido na criação.
                  </p>
                </div>
                <Switch checked={aprovacaoEntrada} onCheckedChange={setAprovacaoEntrada} />
              </div>

              <div className="flex gap-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 p-3 text-xs text-blue-800 dark:text-blue-200">
                <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <p>
                  Na API oficial da Meta os participantes não são adicionados pela empresa: eles entram pelo
                  link de convite, gerado após a criação. Cada grupo comporta até {META_LIMITE_PARTICIPANTES} participantes.
                </p>
              </div>
            </>
          ) : (
            <div className="space-y-2">
              <Label className="dark:text-gray-200" htmlFor="grupo-busca">Participantes iniciais</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                <Input className="pl-8 dark:bg-slate-700 dark:border-slate-600 dark:text-white dark:placeholder:text-gray-400"
                  id="grupo-busca"
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="Buscar contato por nome ou telefone"
                  autoComplete="off"
                />
                {buscando && <Loader2 className="absolute right-2.5 top-2.5 h-4 w-4 animate-spin text-gray-400" />}
              </div>
              {busca.trim().length >= 2 && !buscando && (
                <div className="max-h-40 overflow-y-auto rounded-md border dark:border-slate-700 divide-y dark:divide-slate-700">
                  {resultados.length === 0 ? (
                    <p className="p-2 text-xs text-gray-500 dark:text-gray-400">Nenhum contato encontrado</p>
                  ) : resultados.map((contato) => (
                    <button
                      type="button"
                      key={contato.id}
                      onClick={() => selecionarContato(contato)}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-slate-700 ${contato.telefoneValido ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500'}`}
                    >
                      <span className="font-medium">{contato.nome || contato.telefone}</span>
                      <span className="ml-2 text-xs">{contato.telefoneValido ? `+${contato.telefone}` : '(sem telefone resolvido)'}</span>
                    </button>
                  ))}
                </div>
              )}
              {selecionados.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {selecionados.map((contato) => (
                    <span key={contato.telefone} className="inline-flex items-center gap-1 rounded-full bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200 text-xs pl-2.5 pr-1 py-1">
                      {contato.nome || contato.telefone}
                      <button
                        type="button"
                        aria-label={`Remover ${contato.nome || contato.telefone}`}
                        onClick={() => setSelecionados((prev) => prev.filter((c) => c.telefone !== contato.telefone))}
                        className="rounded-full p-0.5 hover:bg-green-200 dark:hover:bg-green-800"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <Label className="dark:text-gray-200" htmlFor="grupo-participantes">Ou digite os números</Label>
              <Textarea className="dark:bg-slate-700 dark:border-slate-600 dark:text-white dark:placeholder:text-gray-400"
                id="grupo-participantes"
                value={participantesTexto}
                onChange={(e) => setParticipantesTexto(e.target.value)}
                placeholder={'Um número por linha (com DDI e DDD)\nEx:\n5521987654321\n5511912345678'}
                rows={3}
              />
              <p className="text-xs text-muted-foreground dark:text-gray-400">
                {numerosInvalidos.length > 0
                  ? `Número fora do formato internacional (8 a 15 dígitos): ${numerosInvalidos.join(', ')}`
                  : participantes.length > 0
                    ? `${participantes.length} participante(s) único(s)`
                    : 'Busque um contato ou informe os números, um por linha'}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={salvando}>Cancelar</Button>
          <Button onClick={handleCriar} disabled={salvando} className="bg-green-600 hover:bg-green-700">
            {salvando ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Criar grupo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CriarGrupoDialog;
